import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Eye, Wand2, Loader2, Image as ImageIcon, Film, Settings, Users, User, Box, Layers,
  Upload, Trash2, Plus, ChevronLeft, ChevronDown, ChevronRight, Sparkles, Check, X, FolderOpen,
  Mic, MessageSquare, Zap, FolderInput, Play, Pause, Clock, Star, Flame, Lock, Search
} from "lucide-react";
import { getAssetUrl, API } from "@/utils/studioUtils";
import { uploadFileToS3, uploadBlobToS3 } from "@/utils/s3Upload";
import { VoiceRecorder } from "./VoiceRecorder";
import { ExtremeActionPanel } from "./ExtremeActionPanel";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

/**
 * PreviewStage - O1 Reference-to-Video workflow
 * Three-panel layout:
 * - Left: Reference management (characters, style images, video)
 * - Center: Scene preview
 * - Right: Improved prompt + Generate button + Settings
 */
export function PreviewStage({
  scene,
  sceneId,
  projectId,
  setCurrentStage,
  setScene,
  fetchScene,
  // O1 State
  o1Elements,
  setO1Elements,
  styleImages,
  setStyleImages,
  videoReference,
  setVideoReference,
  // useStartFrame removed - always true now
  duration,
  setDuration,
  aspectRatio,
  setAspectRatio,
  negativePrompt,
  setNegativePrompt,
  // Action direction & improved prompt
  actionDirection,
  setActionDirection,
  improvedPrompt,
  setImprovedPrompt,
  // Scene type (action/dialogue)
  sceneType = "action",
  setSceneType,
  // Dialogue mode state
  dialoguePrompt,
  setDialoguePrompt,
  selectedVoiceForDialogue,
  setSelectedVoiceForDialogue,
  // Scene script
  sceneScript = "",
  // Generation
  generating,
  onGenerateAnimation,
  onGenerateDialogue,
  // Elapsed timer
  elapsedTime = 0,
  formatElapsedTime = () => "00:00:00",
  // Character profiles for reference images
  characterProfiles = [],
  // Assets for uploads
  assets = [],
  // Callback to refresh assets after upload
  onAssetsChange
}) {
  const [improvingPrompt, setImprovingPrompt] = useState(false);
  const [promptConfidence, setPromptConfidence] = useState(null);
  const [uploadingStyle, setUploadingStyle] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showStyleLibrary, setShowStyleLibrary] = useState(false);
  const [showCharacterLibrary, setShowCharacterLibrary] = useState({ open: false, elementIndex: -1 });
  const [refImageSearch, setRefImageSearch] = useState(""); // Search filter for reference image modal
  const [avoidPanelOpen, setAvoidPanelOpen] = useState(false);
  const [showModeSwitchModal, setShowModeSwitchModal] = useState(false);
  const [pendingModeSwitch, setPendingModeSwitch] = useState(null);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  
  // Asset library tabs for reference image modal (similar to compose step)
  const [refAssetTab, setRefAssetTab] = useState("character"); // 'sets' | 'character' | 'items'
  const [selectedRefProfile, setSelectedRefProfile] = useState(null); // Selected character profile in modal
  
  // Voice preview state
  const [voiceDuration, setVoiceDuration] = useState(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const voiceAudioRef = useRef(null);

  // Default dialogue prompt
  const DEFAULT_DIALOGUE_PROMPT = "2D cartoon character speaking with subtle lip movements. Minimal head motion, gentle eye blinks. Static body, only mouth animates to speech. Flat animation style, no 3D depth or morphing. Clean simple movements like traditional hand-drawn animation.";

  // Voice assets from project library
  const voiceAssets = assets.filter(a => a.type === 'voice');
  
  // Character and background assets for reference image picker
  const characterAssets = assets.filter(a => a.type === 'character');
  const backgroundAssets = assets.filter(a => a.type === 'background');
  
  // Actor voice recordings from the scene's audio_tracks (preserve all fields)
  const actorRecordings = (scene?.audio_tracks || []).map(track => ({
    id: track.id,
    name: track.name || `${track.character_name} - Recording`,
    file_url: track.file_url,
    type: 'voice',
    isActorRecording: true,
    character_name: track.character_name,
    uploaded_by_name: track.uploaded_by_name,
    is_favorite: track.is_favorite,
    note: track.note,
    uploaded_at: track.uploaded_at
  }));
  
  // Combined voice options: actor recordings first, then library assets
  const allVoiceOptions = [...actorRecordings, ...voiceAssets];
  
  // Get selected voice details (check both actor recordings and assets)
  const selectedVoiceAsset = allVoiceOptions.find(v => v.id === selectedVoiceForDialogue);
  const selectedVoiceUrl = selectedVoiceAsset?.file_url ? getAssetUrl(selectedVoiceAsset.file_url) : null;

  // Collapsible state for instructions panel
  const [instructionsPanelOpen, setInstructionsPanelOpen] = useState(false);
  
  // Load voice duration when selection changes
  useEffect(() => {
    if (selectedVoiceUrl && voiceAudioRef.current) {
      voiceAudioRef.current.src = selectedVoiceUrl;
      voiceAudioRef.current.load();
    } else {
      setVoiceDuration(null);
      setIsPlayingVoice(false);
    }
  }, [selectedVoiceUrl]);
  
  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Toggle voice playback
  const toggleVoicePlayback = () => {
    if (!voiceAudioRef.current) return;
    
    if (isPlayingVoice) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.currentTime = 0;
      setIsPlayingVoice(false);
    } else {
      voiceAudioRef.current.play();
      setIsPlayingVoice(true);
    }
  };

  // Handle tab change with confirmation
  const handleModeChange = (newMode) => {
    if (newMode === sceneType) return;
    setPendingModeSwitch(newMode);
    setShowModeSwitchModal(true);
  };

  // Confirm mode switch
  const confirmModeSwitch = async () => {
    if (pendingModeSwitch) {
      // Show loading state when switching to action mode (needs to generate animation instructions)
      const needsProcessing = pendingModeSwitch === "action" && actionDirection.trim() && !improvedPrompt;
      if (needsProcessing) {
        setIsSwitchingMode(true);
      }
      
      setSceneType(pendingModeSwitch);
      
      // Save the mode to the scene in DB
      try {
        await api.patch(`/scenes/${sceneId}`, {
          preview_mode: pendingModeSwitch
        });
      } catch (error) {
        console.error("Failed to save preview mode:", error);
      }
      
      // Initialize dialogue prompt with default if switching to dialogue
      if (pendingModeSwitch === "dialogue" && !dialoguePrompt) {
        setDialoguePrompt(DEFAULT_DIALOGUE_PROMPT);
      }
      // Run Translate Action when switching to Action mode to generate Animation Instructions
      if (pendingModeSwitch === "action" && actionDirection.trim() && !improvedPrompt) {
        setImprovingPrompt(true);
        try {
          const response = await api.post(`/o1/improve-prompt`, {
            scene_id: sceneId,
            user_description: actionDirection,
            elements: o1Elements,
            item_references: [],
            composite_image_url: scene?.composite_image_url
          });

          if (response.data?.success) {
            const result = response.data.result;
            setImprovedPrompt(result.improved_prompt || "");
            setPromptConfidence(result.confidence);
            toast.success("Animation instructions generated!");
          }
        } catch (error) {
          console.error("Error generating animation instructions:", error);
          // Don't show error toast - user can manually translate
        } finally {
          setImprovingPrompt(false);
          setIsSwitchingMode(false);
        }
      } else {
        setIsSwitchingMode(false);
      }
    }
    setShowModeSwitchModal(false);
    setPendingModeSwitch(null);
  };

  // Calculate total reference slots used (max 7)
  // Always counts: 1 background element
  // Conditional: 1 start frame (if enabled)
  // Character elements: 1 per character + their additional reference images
  // Style images: 1 per image
  const calculateSlotsUsed = () => {
    let slots = 0;
    
    // Background element (always required)
    if (scene?.background_id) {
      slots += 1;
    }
    
    // Start frame - always enabled now
    if (scene?.composite_image_url) {
      slots += 1;
    }
    
    // Character elements + their reference images
    o1Elements.forEach(element => {
      slots += 1; // The element's frontal image
      slots += (element.reference_image_urls?.length || 0); // Additional refs
    });
    
    // Style images
    slots += styleImages.length;
    
    return slots;
  };
  
  const slotsUsed = calculateSlotsUsed();
  const slotsRemaining = 7 - slotsUsed;
  const isAtLimit = slotsRemaining <= 0;

  // Translate Action using AI (renamed from Improve Prompt)
  const handleTranslateAction = async () => {
    if (!actionDirection.trim()) {
      toast.error("Please enter an action direction first");
      return;
    }

    setImprovingPrompt(true);
    try {
      const response = await api.post(`/o1/improve-prompt`, {
        scene_id: sceneId,
        user_description: actionDirection,
        elements: o1Elements,
        item_references: [],
        composite_image_url: scene?.composite_image_url
      });

      if (response.data?.success) {
        const result = response.data.result;
        setImprovedPrompt(result.improved_prompt || "");
        setPromptConfidence(result.confidence);
        toast.success("Action translated!");
      } else {
        toast.error("Failed to translate action");
      }
    } catch (error) {
      console.error("Error translating action:", error);
      toast.error("Failed to translate action");
    } finally {
      setImprovingPrompt(false);
    }
  };

  // Add style image from upload
  const handleAddStyleImage = async (file) => {
    if (!file) return;
    if (isAtLimit) {
      toast.warning("Reference limit reached (7 max). Remove something to add more.");
      return;
    }
    
    setUploadingStyle(true);
    try {
      const asset = await uploadFileToS3(file, projectId, {
        type: 'background',
        name: `style-ref-${Date.now()}`
      });
      setStyleImages(prev => [...prev, asset.file_url]);
      toast.success("Style image added");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingStyle(false);
    }
  };

  // Add style image from asset library
  const handleAddStyleFromLibrary = (assetUrl) => {
    if (isAtLimit) {
      toast.warning("Reference limit reached (7 max). Remove something to add more.");
      return;
    }
    if (styleImages.includes(assetUrl)) {
      toast.warning("This image is already added");
      return;
    }
    setStyleImages(prev => [...prev, assetUrl]);
    toast.success("Style image added");
    setShowStyleLibrary(false);
  };

  // Add video reference
  const handleAddVideoReference = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video must be under 200MB");
      return;
    }
    
    setUploadingVideo(true);
    try {
      toast.info("Uploading video...");
      const asset = await uploadFileToS3(file, projectId, {
        type: 'video',
        name: `video-ref-${Date.now()}`
      });
      setVideoReference(asset.file_url);
      toast.success("Video reference added");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload video");
    } finally {
      setUploadingVideo(false);
    }
  };

  // Add reference image to element
  const addReferenceImage = (elementIndex, imageUrl) => {
    // Check per-element limit (fal.ai allows max 3 reference images per element)
    const currentRefs = o1Elements[elementIndex]?.reference_image_urls || [];
    if (currentRefs.length >= 3) {
      toast.warning("Maximum 3 reference images per character (fal.ai limit)");
      return;
    }
    
    if (isAtLimit) {
      toast.warning("Reference limit reached (7 max). Remove something to add more.");
      return;
    }
    
    setO1Elements(prev => {
      const updated = [...prev];
      const refs = updated[elementIndex].reference_image_urls || [];
      if (refs.includes(imageUrl)) {
        toast.warning("This image is already added");
        return prev;
      }
      updated[elementIndex] = {
        ...updated[elementIndex],
        reference_image_urls: [...refs, imageUrl]
      };
      return updated;
    });
  };

  // Remove reference image from element
  const removeReferenceImage = (elementIndex, imageIndex) => {
    setO1Elements(prev => {
      const updated = [...prev];
      const refs = updated[elementIndex].reference_image_urls.filter((_, i) => i !== imageIndex);
      updated[elementIndex] = {
        ...updated[elementIndex],
        reference_image_urls: refs
      };
      return updated;
    });
  };

  // Delete an entire element
  const deleteElement = (elementIndex) => {
    if (!window.confirm(`Remove ${o1Elements[elementIndex]?.name || 'this element'} from the animation?`)) {
      return;
    }
    setO1Elements(prev => prev.filter((_, i) => i !== elementIndex));
  };

  // Add reference from character profile or asset library
  const handleAddCharacterRef = (elementIndex, imageUrl) => {
    addReferenceImage(elementIndex, imageUrl);
    setShowCharacterLibrary({ open: false, elementIndex: -1 });
    setRefImageSearch(""); // Clear search when closing
  };

  // Get character profile for an element
  const getCharacterProfile = (element) => {
    return characterProfiles.find(p => 
      p.id === element.character_profile_id ||
      p.name?.toLowerCase() === element.name?.toLowerCase()
    );
  };

  // Get all assets from library for style references
  const allAssets = assets;

  return (
    <div className="flex-1 flex h-full overflow-hidden" data-testid="preview-stage">
      {/* Mode Switch Confirmation Modal */}
      <Dialog open={showModeSwitchModal} onOpenChange={setShowModeSwitchModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Switch to {pendingModeSwitch === "dialogue" ? "Dialogue" : pendingModeSwitch === "extreme_action" ? "Extreme Action" : "Action"} Mode?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-zinc-400 space-y-3 pt-2">
                {pendingModeSwitch === "dialogue" ? (
                  <>
                    <p><strong className="text-purple-400">Dialogue mode</strong> is best for shots where a character is primarily talking with minimal movement. Combines subtle movement with realistic lip sync in a single step.</p>
                    <p><strong className="text-indigo-400">Action mode</strong> is better for shots with physical movement, gestures, walking, or dynamic action. You can add lip sync to action clips in the next step.</p>
                  </>
                ) : pendingModeSwitch === "extreme_action" ? (
                  <>
                    <p><strong className="text-orange-400">Extreme Action mode</strong> is designed for fight scenes, fast movement, and precise choreography. Uses a slow-motion technique for smoother, more consistent results.</p>
                    <p className="text-sm">Best for: combat, chase scenes, complex stunts with multiple beats.</p>
                    <p className="text-xs text-amber-400">Note: This mode generates multiple images and videos, so it uses more sparks.</p>
                  </>
                ) : (
                  <>
                    <p><strong className="text-indigo-400">Action mode</strong> is better for shots with physical movement, gestures, walking, or dynamic action. You can add lip sync to action clips in the next step.</p>
                    <p><strong className="text-purple-400">Dialogue mode</strong> is best for shots where a character is primarily talking with minimal movement. Combines subtle movement with realistic lip sync in a single step.</p>
                  </>
                )}
                <p className="text-xs text-zinc-500">Your settings and takes will be saved. You can switch back anytime.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          {/* Loading indicator when switching to Action mode */}
          {isSwitchingMode && (
            <div className="py-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-zinc-800/50 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                <div className="flex-1">
                  <p className="text-sm text-white">Preparing Action Mode...</p>
                  <p className="text-xs text-zinc-400">Generating animation instructions</p>
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full animate-pulse" style={{ width: '70%' }} />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowModeSwitchModal(false)} className="border-zinc-700" disabled={isSwitchingMode}>
              Cancel
            </Button>
            <Button 
              onClick={confirmModeSwitch}
              disabled={isSwitchingMode}
              className={
                pendingModeSwitch === "dialogue" ? "bg-purple-500 hover:bg-purple-600" : 
                pendingModeSwitch === "extreme_action" ? "bg-orange-500 hover:bg-orange-600" : 
                "bg-indigo-500 hover:bg-indigo-600"
              }
            >
              {isSwitchingMode ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Switching...</>
              ) : (
                <>Switch to {pendingModeSwitch === "dialogue" ? "Dialogue" : pendingModeSwitch === "extreme_action" ? "Extreme Action" : "Action"}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Left Panel - 400px wide */}
      <div className="w-[400px] h-full border-r border-zinc-800 bg-zinc-900/50 flex flex-col overflow-hidden">
        {/* Mode Tabs */}
        <div className="p-3 border-b border-zinc-800 shrink-0">
          <Tabs value={sceneType} onValueChange={handleModeChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-800/50">
              <TabsTrigger 
                value="dialogue" 
                className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 gap-1 text-xs"
                data-testid="dialogue-mode-tab"
              >
                <MessageSquare className="w-3 h-3" />
                Dialogue
              </TabsTrigger>
              <TabsTrigger 
                value="action" 
                className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400 gap-1 text-xs"
                data-testid="action-mode-tab"
              >
                <Zap className="w-3 h-3" />
                Action
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Action Mode Content */}
        {sceneType === "action" && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="p-3 border-b border-zinc-800 shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-400" />
                  References
                </h3>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    isAtLimit ? 'text-red-400 border-red-400/50 bg-red-500/10' : 
                    slotsRemaining <= 2 ? 'text-amber-400 border-amber-400/50' : 
                    'text-zinc-400 border-zinc-600'
                  }`}
                >
                  {slotsUsed}/7 slots
                </Badge>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                {isAtLimit ? (
                  <span className="text-red-400">Limit reached - remove items to add more</span>
                ) : (
                  `${slotsRemaining} slot${slotsRemaining !== 1 ? 's' : ''} remaining`
                )}
              </p>
            </div>
        
            <ScrollArea className="flex-1 min-h-0">
          <div className="p-3 space-y-4">
            {/* Character Elements */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-zinc-400">Character Elements ({o1Elements.length}/4)</Label>
              </div>
              
              {o1Elements.length === 0 ? (
                <div className="p-4 rounded-lg border border-dashed border-zinc-700 text-center">
                  <Users className="w-6 h-6 mx-auto text-zinc-600 mb-1" />
                  <p className="text-xs text-zinc-500">No characters in shot</p>
                  <p className="text-[10px] text-zinc-600">Add characters in Compose</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {o1Elements.slice(0, 4).map((element, index) => {
                    const profile = getCharacterProfile(element);
                    
                    return (
                      <Card key={index} className="bg-zinc-800/50 border-zinc-700 group/card">
                        <CardHeader className="p-2 pb-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-purple-400 border-purple-400/50 text-[10px] shrink-0">
                              @Element{index + 1}
                            </Badge>
                            <span className="text-xs text-white truncate flex-1" title={element.name}>
                              {element.name?.length > 10 ? element.name.slice(0, 10) + '…' : element.name}
                            </span>
                            <button
                              onClick={() => deleteElement(index)}
                              className="opacity-0 group-hover/card:opacity-100 p-1 rounded hover:bg-red-500/20 text-red-400 transition-all"
                              title="Remove from animation"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </CardHeader>
                        <CardContent className="p-2 pt-0">
                          {/* Reference Images */}
                          <div className="flex flex-wrap gap-1.5">
                            {/* Primary/Frontal Image */}
                            {element.frontal_image_url && (
                              <div className="w-12 h-12 rounded overflow-hidden border-2 border-purple-500/50 relative group">
                                <img
                                  src={getAssetUrl(element.frontal_image_url)}
                                  alt="Primary"
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-purple-500/30 text-[8px] text-purple-200 flex items-end justify-center pb-0.5 font-medium">
                                  Primary
                                </div>
                              </div>
                            )}
                            {/* Other Reference Images */}
                            {element.reference_image_urls?.map((url, imgIdx) => (
                              <div
                                key={imgIdx}
                                className="w-12 h-12 rounded overflow-hidden border border-zinc-600 relative group"
                              >
                                <img
                                  src={getAssetUrl(url)}
                                  alt={`Ref ${imgIdx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  onClick={() => removeReferenceImage(index, imgIdx)}
                                  className="absolute inset-0 bg-red-500/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                >
                                  <X className="w-4 h-4 text-white" />
                                </button>
                              </div>
                            ))}
                            {/* Add Button - opens dialog with profile images, library, and upload */}
                            {!isAtLimit && (element.reference_image_urls?.length || 0) < 3 && (
                              <button
                                onClick={() => setShowCharacterLibrary({ open: true, elementIndex: index })}
                                className="w-12 h-12 rounded border-2 border-dashed border-zinc-600 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 hover:bg-purple-500/10 transition-colors"
                              >
                                <Plus className="w-4 h-4 text-zinc-500" />
                                <span className="text-[8px] text-zinc-500">Add</span>
                              </button>
                            )}
                          </div>
                          <p className="text-[10px] text-zinc-600 mt-1.5">
                            {(element.reference_image_urls?.length || 0) >= 3 
                              ? 'Max refs for this character' 
                              : isAtLimit 
                                ? 'At total reference limit' 
                                : `${3 - (element.reference_image_urls?.length || 0)} more ref${3 - (element.reference_image_urls?.length || 0) !== 1 ? 's' : ''} allowed`
                            }
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Style Images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <ImageIcon className="w-3 h-3 text-blue-400" />
                  Style Images ({styleImages.length})
                </Label>
                {styleImages.length > 0 && (
                  <Badge variant="outline" className="text-blue-400 border-blue-400/50 text-[10px]">
                    @Image refs
                  </Badge>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2">
                {styleImages.map((url, idx) => (
                  <div
                    key={idx}
                    className="w-16 h-16 rounded-lg overflow-hidden border border-blue-500/30 relative group"
                  >
                    <img
                      src={getAssetUrl(url)}
                      alt={`Style ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0.5 left-0.5 bg-blue-500/80 text-white text-[8px] px-1 rounded">
                      @Image{idx + 2}
                    </div>
                    <button
                      onClick={() => setStyleImages(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute inset-0 bg-red-500/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
                {styleImages.length < 3 && !isAtLimit && (
                  <div className="flex gap-2">
                    {/* Upload Button */}
                    <label className="w-16 h-16 rounded-lg border-2 border-dashed border-blue-500/30 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-500/10 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleAddStyleImage(e.target.files?.[0])}
                        disabled={uploadingStyle}
                      />
                      {uploadingStyle ? (
                        <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-4 h-4 text-blue-400" />
                          <span className="text-[8px] text-blue-400 mt-0.5">Upload</span>
                        </>
                      )}
                    </label>
                    {/* Library Button */}
                    <button
                      onClick={() => setShowStyleLibrary(true)}
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-blue-500/30 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-500/10 transition-colors"
                    >
                      <FolderOpen className="w-4 h-4 text-blue-400" />
                      <span className="text-[8px] text-blue-400 mt-0.5">Library</span>
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-zinc-600 mt-1">Mood boards or style references</p>
            </div>
            
            {/* Video Reference */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <Film className="w-3 h-3 text-orange-400" />
                  Video Reference
                </Label>
                <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-[10px]">
                  @Video1
                </Badge>
              </div>
              
              {videoReference ? (
                <div className="flex items-start gap-2">
                  <div className="w-28 aspect-video rounded-lg overflow-hidden border border-orange-500/30 bg-black">
                    <video
                      src={getAssetUrl(videoReference)}
                      className="w-full h-full object-contain"
                      controls
                      muted
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVideoReference(null)}
                    className="text-red-400 hover:text-red-300 h-6 px-2 text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-16 rounded-lg border-2 border-dashed border-orange-500/30 hover:border-orange-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleAddVideoReference(e.target.files?.[0])}
                    disabled={uploadingVideo}
                  />
                  {uploadingVideo ? (
                    <Loader2 className="w-4 h-4 text-orange-400 animate-spin" />
                  ) : (
                    <div className="text-center">
                      <Upload className="w-4 h-4 mx-auto text-orange-400" />
                      <span className="text-[10px] text-orange-400">Upload Video (max 200MB)</span>
                    </div>
                  )}
                </label>
              )}
              <p className="text-[10px] text-zinc-600 mt-1">Motion/camera style reference</p>
            </div>

            {/* Generation Settings */}
            <div className="border-t border-zinc-700 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-4 h-4 text-zinc-400" />
                <Label className="text-xs text-zinc-400 font-medium">Settings</Label>
              </div>
              
              {/* Duration */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-zinc-300">Duration</span>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="w-20 h-7 text-xs bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 sec</SelectItem>
                    <SelectItem value="10">10 sec</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Aspect Ratio */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-zinc-300">Aspect Ratio</span>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger className="w-20 h-7 text-xs bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="16:9">16:9</SelectItem>
                    <SelectItem value="9:16">9:16</SelectItem>
                    <SelectItem value="1:1">1:1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </ScrollArea>
          </div>
        )}

        {/* Extreme Action Mode Content */}
        {sceneType === "extreme_action" && (
          <ExtremeActionPanel
            scene={scene}
            sceneId={sceneId}
            projectId={projectId}
            actionDirection={actionDirection}
            o1Elements={o1Elements}
            assets={assets}
            characterProfiles={characterProfiles}
            onGenerationComplete={async (videoUrl) => {
              // Update scene state with new video URL
              if (setScene) {
                setScene(prev => ({ ...prev, generated_video_url: videoUrl }));
              }
              // Optionally refresh full scene data
              if (fetchScene) {
                await fetchScene();
              }
              // Navigate to animate step
              setCurrentStage("animate");
            }}
          />
        )}

        {/* Dialogue Mode Content */}
        {sceneType === "dialogue" && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="p-3 border-b border-zinc-800 shrink-0">
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                <Mic className="w-4 h-4 text-purple-400" />
                Voice Selection
              </h3>
              <p className="text-xs text-zinc-500 mt-1">
                Select or upload a voice file for lip sync
              </p>
            </div>
            
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-3 space-y-4">
                {/* Voice File Selection - Reordered: Asset Library, Voice Actor, Record, Upload */}
                <div className="space-y-3">
                  
                  {/* 1. Select from Asset Library */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <FolderOpen className="w-4 h-4 text-indigo-400" />
                      <span className="text-xs text-zinc-300 font-medium">Select from Asset Library</span>
                    </div>
                    <Select value={voiceAssets.find(v => v.id === selectedVoiceForDialogue) ? selectedVoiceForDialogue : ""} onValueChange={setSelectedVoiceForDialogue}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Choose a voice file..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {voiceAssets.length > 0 ? (
                          voiceAssets.map(voice => (
                            <SelectItem key={voice.id} value={voice.id} className="text-white hover:bg-zinc-700">
                              <span className="truncate" title={voice.name}>
                                {voice.name.length > 35 ? voice.name.substring(0, 35) + "..." : voice.name}
                              </span>
                            </SelectItem>
                          ))
                        ) : (
                          <div className="px-2 py-3 text-center text-zinc-500 text-sm">
                            No voice files in library
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* 2. Select Voice Actor File */}
                  {actorRecordings.length > 0 && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-purple-400" />
                        <span className="text-xs text-zinc-300 font-medium">Select a Voice Actor File</span>
                      </div>
                      <Select value={actorRecordings.find(v => v.id === selectedVoiceForDialogue) ? selectedVoiceForDialogue : ""} onValueChange={setSelectedVoiceForDialogue}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue placeholder="Choose actor recording..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {actorRecordings.map(voice => (
                            <SelectItem key={voice.id} value={voice.id} className="text-white hover:bg-zinc-700">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                                <span className="flex-1 truncate" title={voice.name}>
                                  {voice.name.length > 35 ? voice.name.substring(0, 35) + "..." : voice.name}
                                </span>
                                {voice.is_favorite && (
                                  <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />
                                )}
                                {voice.note && (
                                  <MessageSquare className="w-3 h-3 text-blue-400 shrink-0" />
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Show actor note if selected voice has one */}
                      {selectedVoiceAsset?.note && actorRecordings.find(v => v.id === selectedVoiceForDialogue) && (
                        <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <div className="flex items-center gap-1 text-blue-400 text-xs font-medium mb-1">
                            <MessageSquare className="w-3 h-3" />
                            Actor Note
                          </div>
                          <p className="text-sm text-zinc-300">{selectedVoiceAsset.note}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* 3. Record a Voice File */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mic className="w-4 h-4 text-red-400" />
                      <span className="text-xs text-zinc-300 font-medium">Record a Voice File</span>
                    </div>
                    <VoiceRecorder
                      compact
                      showUploadOption={false}
                      onRecordingComplete={async (blob) => {
                        try {
                          toast.info("Uploading recording...");
                          const asset = await uploadBlobToS3(blob, projectId, `voice-recording-${Date.now()}.webm`, {
                            type: 'voice',
                            name: `Voice Recording ${new Date().toLocaleTimeString()}`
                          });
                          if (onAssetsChange) await onAssetsChange();
                          setSelectedVoiceForDialogue(asset.id);
                          toast.success("Recording uploaded!");
                        } catch (err) {
                          toast.error("Failed to upload recording");
                          console.error(err);
                        }
                      }}
                    />
                  </div>
                  
                  {/* 4. Upload a Voice File */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Upload className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-zinc-300 font-medium">Upload a Voice File</span>
                    </div>
                    <label className="block">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            toast.info("Uploading voice file...");
                            const asset = await uploadFileToS3(file, projectId, {
                              type: 'voice',
                              name: file.name || "uploaded_voice"
                            });
                            if (onAssetsChange) {
                              await onAssetsChange();
                            }
                            setSelectedVoiceForDialogue(asset.id);
                            toast.success("Voice file uploaded!");
                          } catch (err) {
                            toast.error("Failed to upload voice file");
                            console.error(err);
                          }
                        }}
                        data-testid="dialogue-upload-voice-input"
                      />
                      <Button variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 gap-2" asChild>
                        <span>
                          <Upload className="w-4 h-4" />
                          Choose File
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>

                {/* Selected voice preview with duration and play button */}
                {selectedVoiceForDialogue && (
                  <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm text-purple-300 min-w-0">
                        <Mic className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {selectedVoiceAsset?.name || "Selected voice"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Duration display */}
                        <div className="flex items-center gap-1 text-xs text-zinc-400">
                          <Clock className="w-3 h-3" />
                          <span className="font-mono">{formatDuration(voiceDuration)}</span>
                        </div>
                        {/* Play/Pause button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={toggleVoicePlayback}
                          className={`h-7 w-7 p-0 rounded-full ${isPlayingVoice ? 'bg-purple-500 text-white hover:bg-purple-600' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                          data-testid="voice-preview-play-btn"
                        >
                          {isPlayingVoice ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                        </Button>
                      </div>
                    </div>
                    {/* Hidden audio element for playback */}
                    <audio
                      ref={voiceAudioRef}
                      onLoadedMetadata={(e) => setVoiceDuration(e.target.duration)}
                      onEnded={() => setIsPlayingVoice(false)}
                      onError={() => setVoiceDuration(null)}
                      preload="metadata"
                    />
                  </div>
                )}

                {/* Info about dialogue mode */}
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-400">
                    <strong className="text-purple-400">Dialogue mode</strong> generates a talking character video directly from your shot image and voice file. Best for conversation shots with minimal movement.
                  </p>
                </div>
              </div>
            </ScrollArea>

            {/* Match Dialogue Button */}
            <div className="p-3 border-t border-zinc-800 bg-zinc-900/80 shrink-0">
              <Button
                onClick={onGenerateDialogue}
                disabled={generating || !selectedVoiceForDialogue}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 gap-2"
                data-testid="generate-dialogue-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating... {formatElapsedTime(elapsedTime)}</>
                ) : (
                  <><MessageSquare className="w-4 h-4" /> Match Dialogue</>
                )}
              </Button>
              {!selectedVoiceForDialogue && (
                <p className="text-[10px] text-amber-400 text-center mt-2">
                  Select a voice file to generate dialogue
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Center - Shot Preview */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 bg-zinc-950/50">
        <div 
          className="relative rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950"
          style={{ 
            aspectRatio: aspectRatio === "16:9" ? "16/9" : aspectRatio === "9:16" ? "9/16" : "1/1",
            width: '100%',
            maxWidth: aspectRatio === "9:16" ? '400px' : 'calc((100vh - 200px) * 16 / 9)',
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          {scene?.composite_image_url ? (
            <img 
              src={getAssetUrl(scene.composite_image_url)} 
              alt="Shot Preview" 
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-zinc-500">
                <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="mb-2">No preview captured yet</p>
                <Button
                  onClick={() => setCurrentStage("compose")}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-300"
                >
                  Go to Compose to capture preview
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {scene?.composite_image_url && (
          <div className="mt-2 flex items-center gap-2 text-xs text-purple-400">
            <Check className="w-3 h-3" />
            This will be used as @Image1 (start frame)
          </div>
        )}
      </div>

      {/* Right Panel - Prompt & Generation */}
      <div className="w-[380px] border-l border-zinc-800 bg-zinc-900/50 flex flex-col overflow-hidden">
        <div className="p-3 border-b border-zinc-800 shrink-0">
          <h3 className="text-sm font-medium text-white flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-indigo-400" />
            Animation Prompt
          </h3>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-4">
            {/* Shot Script (read-only from Compose step) */}
            {sceneScript && (
              <div>
                <Label className="text-xs text-zinc-400 mb-2 block">
                  Shot Script
                </Label>
                <div className="p-2 bg-zinc-800/30 border border-zinc-700/50 rounded-md">
                  <p className="text-xs text-zinc-300 font-mono whitespace-pre-wrap">
                    {sceneScript}
                  </p>
                </div>
              </div>
            )}

            {/* Action Direction (read-only, from Compose step) */}
            {actionDirection && (
              <div>
                <Label className="text-xs text-zinc-400 mb-2 block">
                  Action Direction
                </Label>
                <div className="p-2 bg-zinc-800/30 border border-zinc-700/50 rounded-md">
                  <p className="text-xs text-zinc-300 whitespace-pre-wrap">
                    {actionDirection}
                  </p>
                </div>
              </div>
            )}
            
            {/* Animation Instructions - Collapsible (only shown in Action mode) */}
            {sceneType === "action" && improvedPrompt && (
              <div className="border border-zinc-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setInstructionsPanelOpen(!instructionsPanelOpen)}
                  className="w-full flex items-center justify-between p-2 text-xs text-zinc-400 hover:bg-zinc-800/50 transition-colors"
                  data-testid="instructions-panel-toggle"
                >
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-green-400" />
                    Animation Instructions
                    {promptConfidence && (
                      <Badge 
                        variant="outline" 
                        className={`text-[10px] ml-2 ${
                          promptConfidence === 'high' ? 'text-green-400 border-green-400/50' :
                          promptConfidence === 'medium' ? 'text-yellow-400 border-yellow-400/50' :
                          'text-red-400 border-red-400/50'
                        }`}
                      >
                        {promptConfidence}
                      </Badge>
                    )}
                  </span>
                  {instructionsPanelOpen ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
                {instructionsPanelOpen && (
                  <div className="p-2 pt-0">
                    <Textarea
                      value={improvedPrompt}
                      onChange={(e) => setImprovedPrompt(e.target.value)}
                      className="min-h-[140px] bg-zinc-800/50 border-green-500/30 text-sm font-mono resize-none"
                      data-testid="improved-prompt-textarea"
                    />
                    <p className="text-[10px] text-zinc-600 mt-1">
                      You can edit this prompt before generating
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Dialogue Instructions - Collapsible (only shown in Dialogue mode) */}
            {sceneType === "dialogue" && (
              <div className="border border-zinc-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setInstructionsPanelOpen(!instructionsPanelOpen)}
                  className="w-full flex items-center justify-between p-2 text-xs text-zinc-400 hover:bg-zinc-800/50 transition-colors"
                  data-testid="dialogue-instructions-panel-toggle"
                >
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3 text-purple-400" />
                    Dialogue Instructions
                  </span>
                  {instructionsPanelOpen ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
                {instructionsPanelOpen && (
                  <div className="p-2 pt-0">
                    <Textarea
                      value={dialoguePrompt || DEFAULT_DIALOGUE_PROMPT}
                      onChange={(e) => setDialoguePrompt(e.target.value)}
                      className="min-h-[140px] bg-zinc-800/50 border-purple-500/30 text-sm resize-none"
                      data-testid="dialogue-prompt-textarea"
                    />
                    <p className="text-[10px] text-zinc-600 mt-1">
                      Adjust how the character animation looks during dialogue
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Negative Prompt - Collapsible */}
            <div className="border border-zinc-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setAvoidPanelOpen(!avoidPanelOpen)}
                className="w-full flex items-center justify-between p-2 text-xs text-zinc-400 hover:bg-zinc-800/50 transition-colors"
                data-testid="avoid-panel-toggle"
              >
                <span className="flex items-center gap-1">
                  <X className="w-3 h-3 text-red-400" />
                  Avoid
                </span>
                {avoidPanelOpen ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
              {avoidPanelOpen && (
                <div className="p-2 pt-0">
                  <Textarea
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder="Things to avoid: blur, distortion, morphing..."
                    className="min-h-[60px] bg-zinc-800/50 border-zinc-700 text-xs resize-none"
                    data-testid="negative-prompt-input"
                  />
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
        
        {/* Generate Button - Fixed at bottom (Action mode only) */}
        {sceneType === "action" && (
          <div className="p-3 border-t border-zinc-800 bg-zinc-900/80 shrink-0 space-y-2">
            <Button
              onClick={onGenerateAnimation}
              disabled={generating || !improvedPrompt}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 gap-2"
              data-testid="generate-animation-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating... {formatElapsedTime(elapsedTime)}</>
              ) : (
                <><Wand2 className="w-4 h-4" /> Create Animation</>
              )}
            </Button>
            {!improvedPrompt && (
              <p className="text-[10px] text-amber-400 text-center">
                Animation instructions will be generated automatically when you enter the Preview step.
              </p>
            )}
          </div>
        )}
        
        {/* Back to Compose Button */}
        <div className="p-3 border-t border-zinc-800 bg-zinc-900/80 shrink-0">
          <Button
            onClick={() => setCurrentStage("compose")}
            variant="ghost"
            className="w-full text-zinc-400 hover:text-white gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Compose
          </Button>
        </div>
      </div>

      {/* Style Library Dialog */}
      <Dialog open={showStyleLibrary} onOpenChange={setShowStyleLibrary}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white">Add Style Reference</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Choose from your asset library or upload a new image
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[450px] overflow-y-auto space-y-4">
            {/* Upload Option */}
            <div>
              <p className="text-xs text-zinc-400 mb-2 font-medium">Upload New Image</p>
              <label className="flex items-center justify-center w-full h-20 rounded-lg border-2 border-dashed border-blue-500/30 hover:border-blue-500 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    handleAddStyleImage(e.target.files?.[0]);
                    setShowStyleLibrary(false);
                  }}
                  disabled={uploadingStyle}
                />
                {uploadingStyle ? (
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                ) : (
                  <div className="text-center">
                    <Upload className="w-5 h-5 mx-auto text-blue-400 mb-1" />
                    <span className="text-xs text-blue-400">Click to upload a new image</span>
                  </div>
                )}
              </label>
            </div>
            
            {/* Asset Library - Grouped by Type */}
            {allAssets.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No assets available in library</p>
            ) : (
              <>
                {/* Backgrounds */}
                {allAssets.filter(a => a.type === 'background').length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2 font-medium">Backgrounds / Sets</p>
                    <div className="grid grid-cols-5 gap-2">
                      {allAssets.filter(a => a.type === 'background').map((asset) => (
                        <button
                          key={asset.id || asset.file_url}
                          onClick={() => handleAddStyleFromLibrary(asset.file_url)}
                          className="aspect-square rounded-lg overflow-hidden border border-zinc-700 hover:border-blue-500 transition-colors"
                        >
                          <img
                            src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                            alt={asset.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Characters */}
                {allAssets.filter(a => a.type === 'character').length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2 font-medium">Characters</p>
                    <div className="grid grid-cols-5 gap-2">
                      {allAssets.filter(a => a.type === 'character').map((asset) => (
                        <button
                          key={asset.id || asset.file_url}
                          onClick={() => handleAddStyleFromLibrary(asset.file_url)}
                          className="aspect-square rounded-lg overflow-hidden border border-zinc-700 hover:border-blue-500 transition-colors"
                        >
                          <img
                            src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                            alt={asset.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Props / Items */}
                {allAssets.filter(a => a.type === 'prop').length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2 font-medium">Props / Items</p>
                    <div className="grid grid-cols-5 gap-2">
                      {allAssets.filter(a => a.type === 'prop').map((asset) => (
                        <button
                          key={asset.id || asset.file_url}
                          onClick={() => handleAddStyleFromLibrary(asset.file_url)}
                          className="aspect-square rounded-lg overflow-hidden border border-zinc-700 hover:border-blue-500 transition-colors"
                        >
                          <img
                            src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                            alt={asset.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Other Assets */}
                {allAssets.filter(a => !['background', 'character', 'prop'].includes(a.type)).length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2 font-medium">Other</p>
                    <div className="grid grid-cols-5 gap-2">
                      {allAssets.filter(a => !['background', 'character', 'prop'].includes(a.type)).map((asset) => (
                        <button
                          key={asset.id || asset.file_url}
                          onClick={() => handleAddStyleFromLibrary(asset.file_url)}
                          className="aspect-square rounded-lg overflow-hidden border border-zinc-700 hover:border-blue-500 transition-colors"
                        >
                          <img
                            src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                            alt={asset.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Character Reference Library Dialog - Uses character profiles like Asset Library */}
      <Dialog 
        open={showCharacterLibrary.open} 
        onOpenChange={(open) => {
          setShowCharacterLibrary({ open, elementIndex: -1 });
          setRefAssetTab('character');
          setRefImageSearch('');
          setSelectedRefProfile(null);
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white">
              Add Reference Image for {o1Elements[showCharacterLibrary.elementIndex]?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Browse your asset library or upload a new image
            </DialogDescription>
          </DialogHeader>
          
          {/* Asset Type Tabs - Sets, Characters, Items */}
          <Tabs value={refAssetTab} onValueChange={(tab) => { setRefAssetTab(tab); setSelectedRefProfile(null); }} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-zinc-800/50 w-full grid grid-cols-3 shrink-0">
              <TabsTrigger value="sets" className="text-xs data-[state=active]:bg-indigo-500 gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" />
                Sets
              </TabsTrigger>
              <TabsTrigger value="character" className="text-xs data-[state=active]:bg-indigo-500 gap-1.5">
                <User className="w-3.5 h-3.5" />
                Characters
              </TabsTrigger>
              <TabsTrigger value="items" className="text-xs data-[state=active]:bg-indigo-500 gap-1.5">
                <Box className="w-3.5 h-3.5" />
                Items
              </TabsTrigger>
            </TabsList>
            
            {/* Search Bar */}
            <div className="px-1 py-2 shrink-0">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input
                  type="text"
                  placeholder={`Search ${refAssetTab === 'sets' ? 'sets' : refAssetTab === 'character' ? 'characters' : 'items'}...`}
                  value={refImageSearch}
                  onChange={(e) => setRefImageSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
            
            {/* Tab Content */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2">
                {showCharacterLibrary.elementIndex >= 0 && (() => {
                  const element = o1Elements[showCharacterLibrary.elementIndex];
                  const searchTerm = refImageSearch.toLowerCase().trim();
                  
                  // Helper to get primary image from character profile
                  const getPrimaryImage = (profile) => {
                    const primary = profile.reference_images?.find(img => img.is_primary);
                    return primary || profile.reference_images?.[0];
                  };
                  
                  // Filter assets by type and search term (for Sets and Items)
                  const getFilteredAssets = (type) => {
                    let typeAssets = assets.filter(a => a.type === type);
                    if (searchTerm) {
                      typeAssets = typeAssets.filter(a => 
                        a.name?.toLowerCase().includes(searchTerm) ||
                        a.folder?.toLowerCase().includes(searchTerm)
                      );
                    }
                    // Filter out already used assets
                    return typeAssets.filter(asset => 
                      asset.file_url !== element.frontal_image_url &&
                      !element.reference_image_urls?.includes(asset.file_url)
                    );
                  };
                  
                  // Group assets by folder (for Sets and Items)
                  const groupByFolder = (assetsToGroup) => {
                    const groups = {};
                    assetsToGroup.forEach(asset => {
                      const folder = asset.folder || 'Ungrouped';
                      if (!groups[folder]) groups[folder] = [];
                      groups[folder].push(asset);
                    });
                    return groups;
                  };
                  
                  // Render Sets or Items with folder grouping
                  const renderFolderGroupedAssets = (type, borderColor) => {
                    const filteredAssets = getFilteredAssets(type);
                    const grouped = groupByFolder(filteredAssets);
                    const folderNames = Object.keys(grouped).sort();
                    
                    if (folderNames.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <FolderOpen className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                          <p className="text-zinc-500 text-sm">
                            {searchTerm ? `No results for "${searchTerm}"` : `No ${type === 'background' ? 'sets' : 'items'} available`}
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="space-y-4">
                        {folderNames.map(folder => (
                          <div key={folder}>
                            <p className="text-xs text-zinc-400 mb-2 font-medium flex items-center gap-2">
                              <FolderOpen className="w-3 h-3" />
                              {folder} ({grouped[folder].length})
                            </p>
                            <div className="grid grid-cols-4 gap-2">
                              {grouped[folder].map((asset, idx) => (
                                <button
                                  key={`${folder}-${idx}`}
                                  onClick={() => handleAddCharacterRef(showCharacterLibrary.elementIndex, asset.file_url)}
                                  className={`aspect-square rounded-lg overflow-hidden border-2 ${borderColor} transition-all relative group`}
                                  title={asset.name}
                                >
                                  <img
                                    src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                                    alt={asset.name}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                                    <span className="text-[10px] text-white truncate block">{asset.name}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  };
                  
                  // Render Characters tab using character profiles (like Asset Library)
                  const renderCharacterProfiles = () => {
                    // If a profile is selected, show its images
                    if (selectedRefProfile) {
                      const profileImages = selectedRefProfile.reference_images || [];
                      const availableImages = profileImages.filter(img => 
                        img.file_url !== element.frontal_image_url &&
                        !element.reference_image_urls?.includes(img.file_url)
                      );
                      
                      return (
                        <div>
                          <button
                            onClick={() => setSelectedRefProfile(null)}
                            className="mb-3 text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                          >
                            <ChevronLeft className="w-3 h-3" />
                            Back to characters
                          </button>
                          <div className="text-sm font-medium text-white mb-3">{selectedRefProfile.name}</div>
                          
                          {availableImages.length === 0 ? (
                            <div className="text-center py-8 text-zinc-500 text-sm">
                              No additional images available in this character
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-2">
                              {availableImages.map((img, idx) => {
                                const asset = assets.find(a => a.id === img.asset_id);
                                return (
                                  <button
                                    key={img.asset_id || idx}
                                    onClick={() => handleAddCharacterRef(showCharacterLibrary.elementIndex, img.file_url || asset?.file_url)}
                                    className={`aspect-square rounded-lg overflow-hidden border-2 ${img.is_primary ? 'border-amber-500' : 'border-purple-500/50 hover:border-purple-400'} transition-all relative group`}
                                    title={asset?.name || img.name || `Image ${idx + 1}`}
                                  >
                                    <img
                                      src={getAssetUrl(img.thumbnail_url || img.file_url || asset?.file_url)}
                                      alt={asset?.name || `Image ${idx + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                    {img.is_primary && (
                                      <div className="absolute top-1 left-1 bg-amber-500 text-black text-[8px] px-1 rounded font-medium">
                                        Primary
                                      </div>
                                    )}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                                      <span className="text-[10px] text-white truncate block">{asset?.name || img.name || `Image ${idx + 1}`}</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Show character profiles list
                    const filteredProfiles = characterProfiles.filter(profile => 
                      !searchTerm || profile.name.toLowerCase().includes(searchTerm)
                    );
                    
                    if (filteredProfiles.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <Users className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                          <p className="text-zinc-500 text-sm">
                            {searchTerm ? `No characters matching "${searchTerm}"` : 'No characters available'}
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid grid-cols-3 gap-3">
                        {filteredProfiles.map((profile) => {
                          const primaryImage = getPrimaryImage(profile);
                          const imageCount = profile.reference_images?.length || 0;
                          
                          return (
                            <button
                              key={profile.id}
                              onClick={() => setSelectedRefProfile(profile)}
                              className="aspect-square rounded-lg overflow-hidden border-2 border-zinc-700 hover:border-purple-500 transition-all relative group bg-zinc-800"
                            >
                              {primaryImage ? (
                                <img
                                  src={getAssetUrl(primaryImage.thumbnail_url || primaryImage.file_url)}
                                  alt={profile.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Users className="w-8 h-8 text-zinc-600" />
                                </div>
                              )}
                              {/* Image count badge */}
                              <div className="absolute top-1 right-1 bg-black/70 text-zinc-300 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                                <Layers className="w-2.5 h-2.5" />
                                {imageCount}
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-4">
                                <span className="text-xs text-white truncate block font-medium">{profile.name}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  };
                  
                  // Render based on active tab
                  if (refAssetTab === 'sets') {
                    return renderFolderGroupedAssets('background', 'border-green-500/50 hover:border-green-400');
                  } else if (refAssetTab === 'character') {
                    return renderCharacterProfiles();
                  } else {
                    return renderFolderGroupedAssets('prop', 'border-amber-500/50 hover:border-amber-400');
                  }
                })()}
              </div>
            </ScrollArea>
          </Tabs>
          
          {/* Upload Option - Always visible */}
          <div className="border-t border-zinc-800 pt-3 shrink-0">
            <label className="flex items-center justify-center w-full h-16 rounded-lg border-2 border-dashed border-zinc-700 hover:border-indigo-500 transition-colors cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file && showCharacterLibrary.elementIndex >= 0) {
                    try {
                      const element = o1Elements[showCharacterLibrary.elementIndex];
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("name", `${element.name}-ref-${Date.now()}`);
                      formData.append("type", refAssetTab === 'sets' ? 'background' : refAssetTab === 'items' ? 'prop' : 'character');
                      const res = await api.post(`/projects/${projectId}/assets`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      handleAddCharacterRef(showCharacterLibrary.elementIndex, res.data.file_url);
                      toast.success("Image uploaded and added");
                    } catch (err) {
                      toast.error("Upload failed");
                    }
                  }
                }}
              />
              <div className="text-center">
                <Upload className="w-5 h-5 mx-auto text-zinc-500 mb-1" />
                <span className="text-xs text-zinc-500">Upload new {refAssetTab === 'sets' ? 'set' : refAssetTab === 'items' ? 'item' : 'character'} image</span>
              </div>
            </label>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PreviewStage;
